import { useEffect, useRef } from 'react';
import { useTranslation } from '../i18n';
import { goalService } from '../services/goalService';
import { useToast } from '../contexts/ToastContext';

const POLL_INTERVAL_MS = 60_000;
const SHOWN_STORAGE_KEY = 'riselio:shownReminders';

type ShownReminders = Record<string, string>;

const readShown = (): ShownReminders => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHOWN_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeShown = (shown: ShownReminders) => {
  try {
    localStorage.setItem(SHOWN_STORAGE_KEY, JSON.stringify(shown));
  } catch {
    // Storage can be blocked; a reminder may then show again after a reload.
  }
};

/**
 * Polls for todos whose reminder time has passed and shows one toast per
 * (todo, reminder time). Rescheduling a reminder re-arms it.
 */
export const useDueReminders = (enabled: boolean) => {
  const { t } = useTranslation();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!enabled) return undefined;

    const check = async () => {
      try {
        const todos = await goalService.getDueReminders();
        const shown = readShown();
        let changed = false;
        for (const todo of todos) {
          if (!todo.reminderAt || shown[todo.id] === todo.reminderAt) continue;
          shown[todo.id] = todo.reminderAt;
          changed = true;
          const subGoalId = todo.subGoal?.id || todo.subGoalId;
          toastRef.current.info(tRef.current('due.reminderToast', { title: todo.title }), {
            duration: 0,
            actions: subGoalId
              ? [{ label: tRef.current('due.open'), onClick: () => { window.location.hash = `#/subgoals/${subGoalId}/todos/${todo.id}`; } }]
              : undefined,
          });
        }
        if (changed) writeShown(shown);
      } catch {
        // Best-effort: the next poll retries.
      }
    };

    void check();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [enabled]);
};
